import {
  DataTable,
  DataTableColDef,
  GRID_CHECKBOX_SELECTION_COL_DEF,
  GridEventListener,
  GridRowSelectionModel,
  GridSortModel,
  GridValueGetterParams,
  gridClasses,
  styled,
  useGridApiRef,
} from '@chronosphereio/chrono-ui';
import { get } from 'lodash';
import { useCallback, useEffect, useMemo } from 'react';
import { usePinnedTableLegendColumns } from './hooks/usePinnedTableLegendColumns';
import { useTableLegendScrollbar } from './hooks/useTableLegendScrollbar';
import { LegendProps } from './Legend';
import { LegendCheckbox } from './LegendCheckbox';
import { LegendIcon } from './LegendIcon';
import {
  DEFAULT_LEGEND_DENSITY,
  getAllLegendItemsSelectedState,
  getSingleLegendItemSelectedState,
  LegendDensity,
  LegendItem,
  SelectedLegendItemState,
  TableLegendPinnedColumns,
} from './legend-model';
import { TableLegendCellCheckbox, TableLegendHeaderCheckbox } from './TableLegendCheckboxes';

export type TableLegendColumns = Array<DataTableColDef<LegendItem>>;

export interface TableLegendProps {
  items: LegendItem[];
  height: number;
  width: number;
  density?: LegendDensity;
  selectedItems: SelectedLegendItemState;
  onSelectedItemsChange: (newSelected: SelectedLegendItemState) => void;
  onItemMouseOver?: LegendProps['onItemMouseOver'];
  onItemMouseOut?: LegendProps['onItemMouseOut'];
  columns?: TableLegendColumns;
  /**
   * Allows parent component to persist sort order (ex: save to panel JSON spec).
   */
  sortModel?: GridSortModel;
  /**
   * Updates when the sort model changes due to user interaction.
   */
  onSortModelChange?: (model: GridSortModel) => void;
  /**
   * When true, will not render the "Name" column based on the `label` field. Useful in cases where
   * it will be duplicative with other columns (e.g. when using labels-to-columns in time series chart).
   */
  disableNameColumn?: boolean;

  /**
   * When true, will include a column that shows icons.
   */
  enableIconColumn?: boolean;

  /**
   * Name to use for the icon column header when `enableIconColumn` is true`. If not set, the header
   * name will be blank.
   */
  iconColumnHeaderName?: string;

  /**
   * Enables the mui menu that appears on hover over the column header.
   * @default true
   */
  enableColumnMenu?: boolean;

  /**
   * Customizes the pinned columns.
   *
   * Note that the checkbox column is always pinned to the left and this intentionally cannot be modified.
   */
  pinnedColumns?: TableLegendPinnedColumns;

  /**
   * Custom columns to be inserted after the icon column but before the name column.
   */
  customTableLegendColumnsStart?: TableLegendColumns;
}

export const TABLE_LEGEND_ROW_HEIGHT: Record<LegendDensity, number> = {
  compact: 26,
  standard: 40,
};

const CHECKBOX_COL_WIDTH = 28;

// Narrow width to fit the icon when the header is empty.
const ICON_COL_WIDTH = 28;
// Wider width for when the header has text.
// Picking a width that works well with the word "Status" to start because it is the most likely
// label to be used. We may want to do something more dynamic in the future, but starting simple to
// cover the basic cases we have.
const ICON_COL_WITH_HEADER_WIDTH = 44;

const COLUMNS: Array<DataTableColDef<LegendItem>> = [
  {
    ...GRID_CHECKBOX_SELECTION_COL_DEF,

    // Removes an annoying MUI console.warn
    // https://github.com/mui/mui-x/issues/6519#issuecomment-1828508612
    type: 'string',

    // Have to override checkbox rendering to get the dynamic colors because
    // MUI doesn't pipe dynamic custom props if you try to use `components` to just
    // customize the base checkbox renderer.
    renderHeader: TableLegendHeaderCheckbox,
    renderCell: TableLegendCellCheckbox,
    width: CHECKBOX_COL_WIDTH,
    maxWidth: CHECKBOX_COL_WIDTH,
    minWidth: CHECKBOX_COL_WIDTH,
    disableExpandableCell: true,
  },
];

const ICON_COLUMN: DataTableColDef<LegendItem> = {
  field: 'icon',
  renderCell: ({ row }) => {
    return <LegendIcon icon={row.icon} mode="Table" />;
  },
  align: 'center',
  headerAlign: 'center',
  disableExpandableCell: true,
  sortable: false,
  filterable: false,
};

const NAME_COLUMN: DataTableColDef<LegendItem> = {
  field: 'label',
  headerName: 'Name',
  flex: 1,
  minWidth: 70,
};

/**
 * Converts between the format used for legend selection and the model used for
 * selection within the MUI datagrid.
 */
function selectedLegendItemsToDataTableSelectionModel(
  items: LegendItem[],
  selectedItems: SelectedLegendItemState
): GridRowSelectionModel {
  if (selectedItems === 'ALL') {
    // Sure of the size, so we can pre-allocate the array to save some performance.
    const updatedRowSelection: GridRowSelectionModel = new Array(items.length);
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item) {
        updatedRowSelection[i] = item.id;
      }
    }
    return updatedRowSelection;
  }

  // Can't be sure about the size without iterating because one of the booleans
  // could be false.
  const updatedRowSelection: GridRowSelectionModel = [];
  for (const key in selectedItems) {
    if (selectedItems[key]) {
      updatedRowSelection.push(key);
    }
  }
  return updatedRowSelection;
}

/**
 * Converts between the the model used for selection within the MUI datagrid and
 * the format used for legend selection.
 */
function dataTableSelectionModelToSelectedLegendItems(
  selectionModel: GridRowSelectionModel,
  items: LegendItem[]
): SelectedLegendItemState {
  const updatedRowSelection: SelectedLegendItemState = getAllLegendItemsSelectedState(items, false);
  for (const key of selectionModel) {
    updatedRowSelection[key] = true;
  }
  return updatedRowSelection;
}

/**
 * Get the value using a string-based path.
 */
function tableColumnPathValueGetter({ row, field }: GridValueGetterParams<LegendItem>) {
  return get(row, field);
}

/**
 * Determines which value getter to be used for a given table legend column.
 */
export function getTableColumnValueGetter({ field, valueGetter: origValueGetter }: TableLegendColumns[0]) {
  if (origValueGetter) {
    // Use defined valueGetter if defined
    return origValueGetter;
  }

  // If field includes a ".", it's probably a path, which isn't supported by default in MUI.
  // Return a getter that supports path lookups.
  if (field.includes('.')) {
    return tableColumnPathValueGetter;
  }
}

function useGetTableLegendRowHeight({ density }: Required<Pick<TableLegendProps, 'density'>>) {
  // Doing this useCallback in a hook, so we avoid doing a `useCallback in the same scope as the legend
  // data, which can cause memory leaks. https://www.schiener.io/2024-03-03/react-closures
  return useCallback(() => {
    return TABLE_LEGEND_ROW_HEIGHT[density];
  }, [density]);
}

export function TableLegend({
  items,
  selectedItems: initRowSelection,
  onSelectedItemsChange,
  onItemMouseOver,
  onItemMouseOut,
  height,
  width,
  columns: additionalColumns = [],
  disableNameColumn,
  enableIconColumn,
  iconColumnHeaderName = '',
  density = DEFAULT_LEGEND_DENSITY,
  enableColumnMenu = false,
  pinnedColumns: initPinnedColumns,
  sortModel,
  onSortModelChange,
  customTableLegendColumnsStart = [],
}: TableLegendProps) {
  const apiRef = useGridApiRef();

  const { hasScrollX } = useTableLegendScrollbar({ apiRef });

  useEffect(() => {
    const handleRowMouseEnter: GridEventListener<'rowMouseEnter'> = (params, event) => {
      const index = apiRef.current.getRowIndexRelativeToVisibleRows(params.id);
      onItemMouseOver?.(event, {
        id: `${params.id}`,
        index,
      });
    };

    // API subscription needed because MUI does not expose a prop for this event.
    // The `subscribeEvent` method will automatically unsubscribe in the cleanup function of the `useEffect`.
    return apiRef.current.subscribeEvent('rowMouseEnter', handleRowMouseEnter);
  }, [apiRef, onItemMouseOver]);

  useEffect(() => {
    const handleRowMouseLeave: GridEventListener<'rowMouseLeave'> = (params, event) => {
      const index = apiRef.current.getRowIndexRelativeToVisibleRows(params.id);
      onItemMouseOut?.(event, {
        id: `${params.id}`,
        index,
      });
    };

    // API subscription needed because MUI does not expose a prop for this event.
    // The `subscribeEvent` method will automatically unsubscribe in the cleanup function of the `useEffect`.
    return apiRef.current.subscribeEvent('rowMouseLeave', handleRowMouseLeave);
  }, [apiRef, onItemMouseOut]);

  const rowSelection: GridRowSelectionModel = useMemo(() => {
    return selectedLegendItemsToDataTableSelectionModel(items, initRowSelection);
  }, [initRowSelection, items]);

  // Handle checkbox-based selection using the built-in MUI behavior because it
  // does all the heavy lifting for us.
  const handleCheckboxRowSelectionChange = (selectionModel: GridRowSelectionModel) => {
    const newSelection = dataTableSelectionModelToSelectedLegendItems(selectionModel, items);
    onSelectedItemsChange(newSelection);
  };

  // Handle row click based selection using custom code because the behavior is very different
  // from the built-in MUI behavior.
  const handleRowClick: GridEventListener<'rowClick'> = (params) => {
    const selection = window.getSelection();
    if (selection && selection.toString().trim()) {
      // If there's an active selection with content, the user probably clicked on the row to
      // select content on hover. Do not try to change the selected row.
      return;
    }

    const selectedRows = apiRef.current.getSelectedRows();
    const selectedRowCount = selectedRows.size;

    if (selectedRows.has(params.id) && selectedRowCount === 1) {
      // Row was already selected. Revert to select all.
      onSelectedItemsChange(getAllLegendItemsSelectedState(items));
    } else {
      // Select only this row.
      onSelectedItemsChange(getSingleLegendItemSelectedState(items, `${params.id}`));
    }
  };

  const columns = useMemo(() => {
    const baseColumns = [...COLUMNS];

    if (enableIconColumn) {
      // Keep the column pretty narrow to make the best use of space when there's a blank header
      // name. Give a bit more space when it has a header name, so it's at least partly readable.
      const iconColumnWidth = iconColumnHeaderName ? ICON_COL_WITH_HEADER_WIDTH : ICON_COL_WIDTH;
      baseColumns.push({
        ...ICON_COLUMN,
        headerName: iconColumnHeaderName,
        width: iconColumnWidth,
        maxWidth: iconColumnWidth,
        minWidth: iconColumnWidth,
      });
    }

    // Add customTableLegendColumnsStart after icon column but before name column
    if (customTableLegendColumnsStart.length > 0) {
      baseColumns.push(
        ...customTableLegendColumnsStart.map((column) => {
          return {
            ...column,
            // Do some processing of getters to maintain support for path-based lookups, which
            // existed by default in tanstack, but do not in MUI datagrid.
            valueGetter: getTableColumnValueGetter(column),
          };
        })
      );
    }

    if (!disableNameColumn) {
      baseColumns.push(NAME_COLUMN);
    }

    return [
      ...baseColumns,
      ...additionalColumns.map((column) => {
        return {
          ...column,
          // Do some processing of getters to maintain support for path-based lookups, which
          // existed by default in tanstack, but do not in MUI datagrid.
          valueGetter: getTableColumnValueGetter(column),
        };
      }),
    ];
  }, [additionalColumns, disableNameColumn, enableIconColumn, iconColumnHeaderName, customTableLegendColumnsStart]);

  const { pinnedColumns } = usePinnedTableLegendColumns({
    width,
    pinnedColumns: initPinnedColumns,
    columns,
    hasScrollX,
  });

  const getTableLegendRowHeight = useGetTableLegendRowHeight({ density });

  return (
    <div style={{ height, width }}>
      <TableLegendDataTable
        apiRef={apiRef}
        compact={density === 'compact'}
        enableExpandableCells
        checkboxSelection
        hideFooter
        disableColumnMenu={!enableColumnMenu}
        rows={items}
        columns={columns}
        sortModel={sortModel}
        onSortModelChange={onSortModelChange}
        rowSelectionModel={rowSelection}
        pinnedColumns={pinnedColumns}
        slots={{
          baseCheckbox: LegendCheckbox,
        }}
        // Prevents some unwanted behavior where onSelectionModelChange is called with an empty
        // selection and unexpectedly resets the selection when using the expand series behavior.
        // This seems to be caused by some invalidation logic in MUI combined with the selected
        // items in the table legend potentially being set before the content of the table legend.
        // This likely happens because the selected items are immediately grabbed from context, but
        // the data for the chart is loaded via a query so has some delay.
        // https://github.com/mui/mui-x/issues/5807
        keepNonExistentRowsSelected
        // Using this function instead of `rowHeight` because it avoids having
        // the row height multiplied by a "density factor." We depend on this
        // constant in other layout code and need it to really be constant.
        getRowHeight={getTableLegendRowHeight}
        disableRowSelectionOnClickWithCheckbox={true}
        onRowSelectionModelChange={handleCheckboxRowSelectionChange}
        // This enables us to handle the click on the cells differently than on
        // the checkboxes.
        onRowClick={handleRowClick}
        localeText={{ noRowsLabel: 'No data.' }}
      />
    </div>
  );
}

const TableLegendDataTable = styled(DataTable)(({ theme }) => {
  return {
    [`.${gridClasses.row}`]: {
      cursor: 'pointer',
      '&.Mui-selected': {
        // Remove custom background color for selected items to match designs.
        background: 'none',
      },
    },
    [`.${gridClasses.cell}`]: {
      borderColor: theme.featureFlags.panwRebrandStubYear ? undefined : theme.palette.grey[50],
    },
    [`.${gridClasses.columnHeaders}`]: {
      borderColor: theme.featureFlags.panwRebrandStubYear ? undefined : theme.palette.grey[100],
    },
    [`.${gridClasses.cell}, .${gridClasses.columnHeader}`]: {
      padding: theme.spacing(0, 0.5),
    },
    // Ensure multi col sort icon badges have enough space for the number indicator
    [`.${gridClasses.columnHeader} .MuiBadge-standard`]: {
      fontSize: '0.5rem',
      marginTop: theme.spacing(0.5),
    },
    // Adjust the positioning of the menu icon button to account for the updated padding in the column headers.
    [`.${gridClasses['columnHeader']} .${gridClasses.menuIcon}`]: {
      marginRight: '-2px',
    },
    [`.${gridClasses['columnHeader--alignRight']} .${gridClasses.menuIcon}`]: {
      marginLeft: '-2px',
      marginRight: 'auto',
    },
    [`& .${gridClasses.cell}:hover`]: {
      [`& .${gridClasses.cellContent}`]: {
        // Padding above minus 1px to account for borders.
        paddingLeft: `calc(${theme.spacing(0.5)} - 1px) !important`,
        paddingRight: `calc(${theme.spacing(0.5)} - 1px) !important`,
      },
    },
  };
});
